import { Clock8Icon, ChevronUp, ChevronDown } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { forwardRef, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface TimeInputProps {
  id?: string;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

// Convert 24-hour format to 12-hour format with AM/PM
const convertTo12Hour = (time24: string): { hour: number; minute: number; period: 'AM' | 'PM' } => {
  if (!time24) return { hour: 12, minute: 0, period: 'AM' };

  const [hours, minutes] = time24.split(':').map(Number);
  const period: 'AM' | 'PM' = hours >= 12 ? 'PM' : 'AM';
  const hour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return { hour, minute: minutes, period };
};

// Convert 12-hour format to 24-hour format
const convertTo24Hour = (hour: number, minute: number, period: 'AM' | 'PM'): string => {
  let hour24 = hour;

  if (period === 'AM') {
    if (hour === 12) hour24 = 0;
  } else {
    if (hour !== 12) hour24 = hour + 12;
  }

  return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

// Format time for display
const formatTimeDisplay = (time24: string): string => {
  if (!time24) return 'Select time';

  const { hour, minute, period } = convertTo12Hour(time24);
  return `${hour}:${minute.toString().padStart(2, '0')} ${period}`;
};

export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(
  ({ id, label, value, onChange, placeholder, required, disabled, className }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const { hour, minute, period } = convertTo12Hour(value || '');

    const [selectedHour, setSelectedHour] = useState(hour);
    const [selectedMinute, setSelectedMinute] = useState(minute);
    const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>(period);

    // Update local state when value prop changes
    useEffect(() => {
      if (value) {
        const { hour, minute, period } = convertTo12Hour(value);
        setSelectedHour(hour);
        setSelectedMinute(minute);
        setSelectedPeriod(period);
      }
    }, [value]);

    const handleTimeChange = (newHour: number, newMinute: number, newPeriod: 'AM' | 'PM') => {
      const time24 = convertTo24Hour(newHour, newMinute, newPeriod);
      onChange?.(time24);
    };

    const incrementHour = () => {
      const newHour = selectedHour === 12 ? 1 : selectedHour + 1;
      setSelectedHour(newHour);
      handleTimeChange(newHour, selectedMinute, selectedPeriod);
    };

    const decrementHour = () => {
      const newHour = selectedHour === 1 ? 12 : selectedHour - 1;
      setSelectedHour(newHour);
      handleTimeChange(newHour, selectedMinute, selectedPeriod);
    };

    const incrementMinute = () => {
      const newMinute = (selectedMinute + 1) % 60;
      setSelectedMinute(newMinute);
      handleTimeChange(selectedHour, newMinute, selectedPeriod);
    };

    const decrementMinute = () => {
      const newMinute = selectedMinute === 0 ? 59 : selectedMinute - 1;
      setSelectedMinute(newMinute);
      handleTimeChange(selectedHour, newMinute, selectedPeriod);
    };

    const togglePeriod = () => {
      const newPeriod = selectedPeriod === 'AM' ? 'PM' : 'AM';
      setSelectedPeriod(newPeriod);
      handleTimeChange(selectedHour, selectedMinute, newPeriod);
    };

    return (
      <div className={cn('w-full space-y-2', className)}>
        {label && (
          <Label htmlFor={id} className="text-sm font-semibold">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              id={id}
              type="button"
              disabled={disabled}
              className={cn(
                'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                !value && 'text-muted-foreground'
              )}
            >
              <span className="flex items-center gap-2">
                <Clock8Icon className="h-4 w-4 opacity-50" />
                {value ? formatTimeDisplay(value) : placeholder || 'Select time'}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="flex items-center justify-center gap-2 p-4">
              {/* Hour Selector */}
              <div className="flex flex-col items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={incrementHour}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <div className="flex h-16 w-16 items-center justify-center rounded-md text-secondary border-2 border-primary bg-primary/10 text-2xl font-bold">
                  {selectedHour.toString().padStart(2, '0')}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={decrementHour}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>

              <span className="text-2xl font-bold">:</span>

              {/* Minute Selector */}
              <div className="flex flex-col items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={incrementMinute}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <div className="flex h-16 w-16 items-center justify-center rounded-md text-secondary border-2 border-primary bg-primary/10 text-2xl font-bold">
                  {selectedMinute.toString().padStart(2, '0')}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={decrementMinute}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>

              {/* AM/PM Toggle */}
              <div className="flex flex-col items-center gap-1">
                <Button
                  variant={selectedPeriod === 'AM' ? 'secondary' : 'outline-solid'}
                  size="sm"
                  className="h-[30px] w-16 text-xs font-semibold"
                  onClick={() => {
                    if (selectedPeriod !== 'AM') togglePeriod();
                  }}
                >
                  AM
                </Button>
                <Button
                  variant={selectedPeriod === 'PM' ? 'secondary' : 'outline-solid'}
                  size="sm"
                  className="h-[30px] w-16 text-xs font-semibold"
                  onClick={() => {
                    if (selectedPeriod !== 'PM') togglePeriod();
                  }}
                >
                  PM
                </Button>
              </div>
            </div>

            <div className="border-t p-3">
              <Button
                className="w-full"
                onClick={() => {
                  // Always set the current selected time when Done is clicked
                  handleTimeChange(selectedHour, selectedMinute, selectedPeriod);
                  setIsOpen(false);
                }}
              >
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Hidden input for form compatibility */}
        <input
          ref={ref}
          type="hidden"
          id={`${id}-hidden`}
          value={value || ''}
        />
      </div>
    );
  }
);

TimeInput.displayName = 'TimeInput';
